import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, X, Shield, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MODULES = [
  "dashboard", "properties", "tax", "tax-engine", "other-tax",
  "certificates", "notices", "grievances", "approvals",
  "governance", "cashbook", "reports", "village-areas",
  "users", "settings",
];

export default function UsersPage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  const [rights, setRights] = useState([]);
  const load = () => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/users", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user]);

  const save = async () => {
    try {
      await api.patch(`/users/${picked.id}/rights`, { modules: rights });
      toast.success("Rights updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (uid) => {
    if (!confirm("Delete user?")) return;
    try { await api.delete(`/users/${uid}`); load(); setPicked(null); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Administration</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "उपयोगकर्ता प्रबंधन" : "User Management"}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Role</th><th className="text-right px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} onClick={() => { setPicked(u); setRights(u.module_rights || []); }}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${picked?.id === u.id ? "bg-[#0A3161]/5" : ""}`}>
                    <td className="px-4 py-3 font-medium">{u.full_name}</td>
                    <td className="px-4 py-3 text-xs">{u.email}</td>
                    <td className="px-4 py-3 capitalize text-xs"><span className="px-2 py-0.5 rounded bg-slate-100">{u.role}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); remove(u.id); }} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-5">
            {picked ? (
              <div data-testid="user-rights">
                <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-[#0A3161]" /><h3 className="font-heading font-bold">{picked.full_name}</h3></div>
                <div className="text-xs text-slate-500">{picked.email}</div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">Module Rights</div>
                <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
                  {MODULES.map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                      <input type="checkbox" checked={rights.includes(m)}
                        data-testid={`right-${m}`}
                        onChange={(e) => {
                          if (e.target.checked) setRights([...rights, m]);
                          else setRights(rights.filter((x) => x !== m));
                        }} />
                      <span className="capitalize">{m.replace(/-/g, " ")}</span>
                    </label>
                  ))}
                </div>
                <button onClick={save} data-testid="rights-save"
                  className="mt-4 w-full px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
                  Save Rights
                </button>
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-10 text-center">Select a user</div>
            )}
          </div>
        </div>
      </div>
    </OfficerLayout>
  );
}
